import { useEffect, useState } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import {
  Stack,
  SelectChangeEvent,
  Typography,
  Tabs,
  Tab,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material'
import { LoadingButton, TabContext, usePagination } from '@mui/lab'
import Select from '@/components/form-control/Select'
import { motion } from 'framer-motion'
import { useSearchCisFertigation } from '@/features/config/hooks/cisFertigation'
import { useAuthContext } from '@/features/auth/context'
import useTranslation from '@/hooks/useTranslation'
import { usePermission } from '@/hooks/permissions'
import { SortBy, flyInVariantsDashboard } from '@/constant/common'
import {
  CharacterPlusIcon,
  LeftArrow,
  RefreshOneIcon,
} from '@/components/icons'
import * as yup from 'yup'

import TableFooter from '@/components/common/Table/TableFooter'
import ButtonField from '@/components/form-control/ButtonField'
import { BoxLoading, RenderIf } from '@/components/common'

import NoData from '@/components/common/NoData'
import { DATA_PAGINATION, OptionItem, SearchParams } from '@/types'
import { CONFIG_ROUTES } from '@/features/config/routes'
import useSearchHydraulicId from '@/features/config/hooks/hydraulic/useSearchHydraulicId'

import classes from './styles.module.scss'
import FertigationTable from './FertigationTable'
import { useConfigUI } from '../../context'
import useDetailCisFertigationMode from '../../hooks/cisFertigation/useDetailCisFertigationMode'
import { useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { FERTIGATION_LINE_TYPE, FertigationLineType } from './types'
import { KeyFormCisFertigation } from '../../components/sidebar/CisFertigationView/CisFertigationView.constant'
import cisFertigationDataConfig from '@/api/config/cisFertigationDataConfig'
import { IFormCisFertigationMode } from '@/types/cisFertigation'
import useUpdateCisFertigationMode from '../../hooks/cisFertigation/useUpdateCisFertigationMode'

type FormValues = {
  fertigationType: {
    id: string
    name: string
  }

  [KeyFormCisFertigation.Fertiliser]?: any
  [KeyFormCisFertigation.PumpStart]?: any
}

const FertigationConfig = () => {
  const MotionDiv = motion.div

  const { t } = useTranslation()
  const { isViewOnly } = usePermission()
  const navigate = useNavigate()
  const { interfaceId: _interfaceId } = useAuthContext()
  const interfaceId = Number(_interfaceId)

  const { pipeId: _pipeId } = useParams<{ pipeId: string }>()
  const pipeId = Number(_pipeId)

  const schema = yup
    .object({
      fertigationType: yup
        .object({
          id: yup.string().required(),
          name: yup.string().required(),
        })
        .required(`${t('commonWordControlValve')} `),
    })
    .required()

  const { openSidebar, setSidebarView, openModal, setModalView } = useConfigUI()
  const themeMode = useSelector(selectTheme)

  const [searchParams, setSearchParams] = useState<SearchParams>({
    PageSize: 10,
    PageIndex: 1,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const { data, isFetching: isFetchingCisFertigation } =
    useSearchCisFertigation(interfaceId, pipeId, searchParams)
  const {
    data: dataHydraulic,
    isFetching: isLoadingHydraulic,
    error: errorHydraulic,
  } = useSearchHydraulicId(interfaceId, pipeId)

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
  })
  const {
    control,
    formState: { errors },
  } = form

  const selectedFertigationType = useWatch({
    control,
    name: 'fertigationType',
  })

  const { data: fertigationMode, isFetching: isFetchingCisFertigationMode } =
    useDetailCisFertigationMode(interfaceId, pipeId)

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const page = parseInt(event.target.value, 10)
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      PageSize: page,
      PageIndex: 1,
    })
  }

  const { items } = usePagination({
    count: data?.totalPage,
    page: currentPage,
    onChange(_event: any, page: any) {
      setCurrentPage(page)
      setSearchParams({
        ...searchParams,
        PageIndex: page,
      })
    },
  })

  useEffect(() => {
    let dataItem = FERTIGATION_LINE_TYPE[0]

    if (fertigationMode) {
      const foundItem = FERTIGATION_LINE_TYPE.find(
        (item) => item.id == fertigationMode.fertigationMode,
      )
      if (foundItem) {
        dataItem = foundItem
      }
    }

    form.reset({
      fertigationType: {
        id: dataItem.id,
        name: dataItem.name,
      },
    })
  }, [fertigationMode, form])

  const handleDesc = (sortBy: SortBy, sortDirection: 'desc' | 'asc') => {
    setSearchParams({
      ...searchParams,
      SortBy: sortBy,
      SortDirection: sortDirection,
    })

    const newSearchParams = {
      SortBy: sortBy,
      SortDirection: sortDirection,
    }

    const combinedSearchParams = { ...newSearchParams }

    // Convert the combined search params to a query string
    const queryString = new URLSearchParams(combinedSearchParams).toString()

    // Navigate to the new URL
    navigate(`?${queryString}`)
    return
  }

  const handleAddFertigation = () => {
    // ViewOnly users cannot add fertigation
    if (isViewOnly) return

    openSidebar()
    setSidebarView({
      type: 'CIS_FERTIGATION_VIEW',
      pipeId,
      isParallelMode: fertigationMode.fertigationMode == 1,
    })
  }

  const handleEditFertigation = (id: string) => {
    // ViewOnly users cannot edit fertigation
    if (isViewOnly) return

    openSidebar()
    setSidebarView({
      type: 'CIS_FERTIGATION_VIEW',
      id,
      pipeId,
      isParallelMode: fertigationMode.fertigationMode == 1,
    })
  }

  const handleDelete = (id: string) => {
    // ViewOnly users cannot delete fertigation
    if (isViewOnly) return

    openModal()
    setModalView({
      type: 'DELETE_CIS_FERTIGATION',
      id,
      interfaceId: interfaceId + '',
    })
  }

  const getGetOption = (key: KeyFormCisFertigation) => {
    const interfaceIdStr = interfaceId!.toString()
    switch (key) {
      case KeyFormCisFertigation.Fertiliser:
        return cisFertigationDataConfig.getFertiliserOption(interfaceIdStr)
      case KeyFormCisFertigation.PumpStart:
        return cisFertigationDataConfig.getPumpStartOption(interfaceIdStr)
      default:
        return Promise.resolve({ data: [] })
    }
  }

  const getOptions = (key: KeyFormCisFertigation) => async () => {
    let data: DATA_PAGINATION | undefined

    const dataOption = await getGetOption(key)
      .then((res) => res.data)
      .catch(() => [])
    const dataMapper = dataOption.map((it: OptionItem) => ({
      ...it,
      label: it.name,
    }))

    data = {
      data: dataMapper,
      page: 1,
      size: dataOption.length,
      totalCount: dataOption.length,
      totalPage: 1,
    }
    return data
  }

  const loadAndSetSelectValue = async (
    key: KeyFormCisFertigation.Fertiliser | KeyFormCisFertigation.PumpStart,
    id?: number,
  ) => {
    if (!id) return

    const options = await getGetOption(key).then((res) => res.data)

    const found = options.find((it: any) => it.id === id)
    if (!found) return

    form.setValue(key, {
      ...found,
      label: found.name,
    })
  }

  useEffect(() => {
    if (!fertigationMode) return

    loadAndSetSelectValue(
      KeyFormCisFertigation.Fertiliser,
      fertigationMode.fertiliserId,
    )

    loadAndSetSelectValue(
      KeyFormCisFertigation.PumpStart,
      fertigationMode.pumpStartId,
    )
  }, [fertigationMode])

  const { mutate: editFertigationMode, isLoading: loadingUpdateMode } =
    useUpdateCisFertigationMode()

  const handleSave = (data: any) => {
    const fertigationType = data.fertigationType
    const pumpStart = data[KeyFormCisFertigation.PumpStart]
    const fertigation = data[KeyFormCisFertigation.Fertiliser]
    if (isViewOnly) return
    if (!pipeId) return
    const formData: IFormCisFertigationMode = {
      interfaceId: interfaceId + '',
      pipeId: pipeId + '',
      fertigationMode: fertigationType.id,
      fertiliserId: fertigationType.id == 0 ? fertigation?.id : '',
      pumpStartId: fertigationType.id == 0 ? pumpStart?.id : '',
    }
    editFertigationMode(formData)
  }

  if (!isLoadingHydraulic) {
    let flagError = false
    if (errorHydraulic) flagError = true
    if (dataHydraulic && !!dataHydraulic?.parentId) flagError = true
    if (flagError) navigate(CONFIG_ROUTES.dataConfig.to)
  }

  return (
    <Stack className={classes.wrapper}>
      <BoxLoading isShow={isLoadingHydraulic} />
      <RenderIf isShow={!isLoadingHydraulic}>
        <Stack className={classes.header}>
          <Stack
            className={classes.arrowLeftIcon}
            component={Link}
            to={CONFIG_ROUTES.dataConfig.to}
          >
            <LeftArrow />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography className={classes.headTitle}>
              {`${t('commonWordFertigation')} - `}
            </Typography>
            <Typography className={classes.txtDescription}>
              {dataHydraulic?.description}
            </Typography>
          </Stack>
        </Stack>
        <Stack className={classes.container}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Box
              className={classes.boxGlobal}
              sx={{
                border: {
                  ...(themeMode === mode_type.dark
                    ? { border: '1px solid #2a4552' }
                    : { border: '1px solid #d7d7d7' }),
                },
              }}
            >
              <Stack
                className={classes.groupHeader}
                direction="row"
                justifyContent="space-between"
              >
                <Typography
                  className={classes.txtGlobal}
                  color="primary.contrastText"
                >
                  {t('Fertigation Line Setup')}
                </Typography>
                <LoadingButton
                  loading={loadingUpdateMode}
                  type="submit"
                  variant={isViewOnly ? 'outlined' : 'primary'}
                  disabled={isViewOnly}
                  sx={{
                    opacity: isViewOnly ? 0.6 : 1,
                    cursor: isViewOnly ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t('commonActionsSave')}
                </LoadingButton>
              </Stack>
              <Stack p="15px">
                <Select
                  required
                  isHasMore
                  filterFunc={false}
                  label={t('Fertigation Line Type')}
                  name="fertigationType"
                  bindLabel="name"
                  bindKey="id"
                  placeholder="Fertigation Line Type"
                  form={form}
                  options={FERTIGATION_LINE_TYPE}
                  maxWidth={'442px'}
                  sx={{
                    width: { xs: '442px' },
                  }}
                />
                {selectedFertigationType?.id == FERTIGATION_LINE_TYPE[0].id && (
                  <Select
                    label={t('Injection Valve')}
                    name={KeyFormCisFertigation.Fertiliser}
                    bindLabel="label"
                    bindKey="label"
                    placeholder="Select one"
                    form={form}
                    getOptions={getOptions(KeyFormCisFertigation.Fertiliser)}
                    maxWidth={'442px'}
                    sx={{
                      width: { xs: '442px' },
                    }}
                  />
                )}
                {selectedFertigationType?.id == FERTIGATION_LINE_TYPE[0].id && (
                  <Select
                    isHasMore
                    filterFunc={false}
                    label={t('Fertigation Pump')}
                    name={KeyFormCisFertigation.PumpStart}
                    bindLabel="name"
                    bindKey="id"
                    placeholder="Fertigation Line Type"
                    form={form}
                    getOptions={getOptions(KeyFormCisFertigation.PumpStart)}
                    maxWidth={'442px'}
                    sx={{
                      width: { xs: '442px' },
                    }}
                  />
                )}
              </Stack>
            </Box>
            {!data && (
              <Stack className={classes.circularProgress}>
                <CircularProgress />
              </Stack>
            )}
          </form>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={{
            padding: '7px 24px',
          }}
        >
          <MotionDiv
            initial="initialRight"
            animate="animate2"
            variants={flyInVariantsDashboard}
          ></MotionDiv>
          <MotionDiv
            initial="initialLeft"
            animate="animate2"
            variants={flyInVariantsDashboard}
          >
            <ButtonField
              startIcon={<CharacterPlusIcon />}
              style={{
                height: '32px',
                minWidth: '89px',
                borderRadius: '5px',
                opacity: isViewOnly ? 0.6 : 1,
              }}
              name={t('commonActionAddNew')}
              onClick={handleAddFertigation}
              disabled={isViewOnly}
            />
          </MotionDiv>
        </Stack>

        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
          className={classes.content}
        >
          <BoxLoading isShow={isFetchingCisFertigation} />

          <RenderIf isShow={!isFetchingCisFertigation}>
            <RenderIf isShow={data && data?.list.length > 0}>
              <FertigationTable
                rows={data?.list || []}
                onEditItem={handleEditFertigation}
                onDeleteItem={handleDelete}
                handleSort={handleDesc}
                isParallelMode={fertigationMode?.fertigationMode == '1'}
              />
            </RenderIf>
            <RenderIf isShow={data && data.totalCount === 0}>
              <NoData text={t('noData')} />
            </RenderIf>
          </RenderIf>
        </MotionDiv>
        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
        >
          <Stack
            sx={{
              display: 'flex',
              bottom: '0',
              justifyContent: 'end',
              flexGrow: '1',
            }}
          >
            <TableFooter
              page={data?.page}
              totalCount={data?.totalCount}
              totalPage={data?.totalPage}
              rowsPerPage={data?.size ?? 0}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              items={items}
            />
          </Stack>
        </MotionDiv>
      </RenderIf>
    </Stack>
  )
}

export default FertigationConfig
